import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import OrganisationResource from '@/api/Dara/OrganisationResource'
import { oneHourInMS } from '@/composable/useDatesAndTimes'
import { organisationKeys } from '@/composable/queries/keys'

interface CombinePdfsData {
  email_to_notify: string
  type: string
  file_type: string
  programme_id: number
  student_ids: number[]
  is_duplex_printing_required: boolean
}

export interface CombinePdfsForGroupsData {
  programme_id: number
  organisation_academic_year_id: number | 'all'
  year_group_id: number
  sub_group_id?: number
  type: string
  file_type: string
}

export const useInvalidateQueries = () => {
  const queryClient = useQueryClient()

  const invalidateOrganisationResourceQuery = (organisationId: number) => {
    queryClient.invalidateQueries({
      queryKey: organisationKeys.resources(ref(organisationId)),
    })
  }

  return {
    invalidateOrganisationResourceQuery,
  }
}

export const useOrganisationResourceQuery = (
  organisationId: Ref<number>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: organisationKeys.resources(organisationId),
    queryFn: () => OrganisationResource.getRecent(organisationId.value),
    staleTime: oneHourInMS,
    enabled,
  })

export const useCombinePdfs = (
  organisationId: Ref<number>,
  programmeId: Ref<number>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (combinePdfsData: CombinePdfsData) => OrganisationResource.requestCombinePdfs(organisationId.value, programmeId.value, combinePdfsData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.resources(ref(organisationId)),
      })
    },
  })
}

export const useCombinePdfsForGroups = (
  organisationId: Ref<number>,
  programmeId: Ref<number>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (combinePdfsForGroupsData: CombinePdfsForGroupsData) => OrganisationResource.requestCombinePdfsForGroups(organisationId.value, programmeId.value, combinePdfsForGroupsData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organisationKeys.resources(ref(organisationId)),
      })
    },
  })
}
